/*
Reglas de lectura de los destacados de ChileAutos de un vehículo.

El vencimiento se calcula al leer, contra `expiresAt`: no hay proceso que marque
los vencidos. TopSpot y Showcase duran 7 días, TopNitro los días de su plan y
Premium no vence mientras el aviso siga publicado.
*/
import type Vehicle from '@/interfaces/Vehicle'
import type { ChaAppliedProduct } from '@/interfaces/ChaProducts'

const DAY_MS = 24 * 60 * 60 * 1000

// Premium es el único reutilizable; el resto se gasta al aplicarlo.
export const isChaConsumable = (code: string): boolean => code.toLowerCase() !== 'premium'

export const isChaProductActive = (product: ChaAppliedProduct, now: Date = new Date()): boolean =>
  !product.expiresAt || new Date(product.expiresAt).getTime() > now.getTime()

// Días que le quedan, redondeando hacia arriba. `null` si no vence.
export const chaDaysLeft = (product: ChaAppliedProduct, now: Date = new Date()): number | null =>
  product.expiresAt ? Math.max(0, Math.ceil((new Date(product.expiresAt).getTime() - now.getTime()) / DAY_MS)) : null

export const activeChaProducts = (vehicle: Pick<Vehicle, 'external_chileautos'>, now: Date = new Date()) =>
  (vehicle.external_chileautos?.products ?? []).filter((product) => isChaProductActive(product, now))

/*
TopSpot/Showcase/TopNitro activos. Despublicar o vender el vehículo no los
devuelve: se pierden los días que quedan y el cupo. Se usa para avisar antes.
*/
export const activeChaConsumables = (vehicle: Pick<Vehicle, 'external_chileautos'>, now: Date = new Date()) =>
  activeChaProducts(vehicle, now).filter((product) => isChaConsumable(product.code))

export const chaDaysLeftLabel = (days: number | null): string =>
  days === null ? 'sin vencimiento' : days <= 1 ? 'queda 1 día' : `quedan ${days} días`

/*
Texto para avisar, antes de despublicar, archivar o vender, que se pierden los
destacados activos. `null` si no hay ninguno. Sólo mira los aplicados desde
Veekls: los del portal de ChileAutos no se pueden ver.
*/
export const chaConsumablesWarning = (
  vehicle: Pick<Vehicle, 'external_chileautos'>,
  now: Date = new Date()
): string | null => {
  const lost = activeChaConsumables(vehicle, now)

  if (lost.length === 0) return null

  const detail = lost.map((product) => `${product.name} (${chaDaysLeftLabel(chaDaysLeft(product, now))})`).join(', ')

  return `Este vehículo tiene destacados activos en ChileAutos: ${detail}. Se pierden los días restantes y el cupo no se reembolsa.`
}

/*
Filtro de destacados del listado de Publicaciones. Cuenta sólo los activos y
aplicados desde Veekls, igual que las etiquetas ★ del listado: un aviso con un
destacado del portal de ChileAutos aparece como "sin destacado".
*/
export const CHA_PRODUCT_FILTER_OPTIONS: Record<string, string> = {
  any: 'Con algún destacado',
  none: 'Sin destacado',
  premium: 'Premium',
  topspot: 'TopSpot',
  showcase: 'Showcase',
  topspot_nitro: 'TopNitro',
}

export const matchesChaProductFilter = (
  vehicle: Pick<Vehicle, 'external_chileautos'>,
  filter: string,
  now: Date = new Date()
): boolean => {
  if (!filter) return true

  const codes = activeChaProducts(vehicle, now).map((product) => product.code.toLowerCase())

  if (filter === 'any') return codes.length > 0

  if (filter === 'none') return codes.length === 0

  return codes.includes(filter)
}
